import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  InputCompound,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { useTheme } from '@/contexts/theme-context';

const DIALOG_WIDTH = 420;
const DIALOG_PADDING = 20;
const INPUT_SIZE = 40;
const TITLE_MAX_LENGTH = 80;

interface AgentThreadRenameDialogProps {
  onOpenChange: (open: boolean) => void;
  onSubmit: (title: string) => void;
  open: boolean;
  title: string | null;
}

export const AgentThreadRenameDialog: React.FC<
  AgentThreadRenameDialogProps
> = ({ onOpenChange, onSubmit, open, title }) => {
  const theme = useTheme();
  const inputRef = useRef<React.ComponentRef<typeof InputCompound.Area>>(null);
  const [draft, setDraft] = useState(title ?? '');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(title ?? '');
    }
  }

  const trimmed = draft.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== (title ?? '');

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onSubmit(trimmed);
    onOpenChange(false);
  };

  const placeholder = i18n.t(
    'sales_portal.superintelligence.rename_placeholder',
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <Dialog.Content
        onOpenAutoFocus={(event: Event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
        paddingHorizontal={0}
        paddingVertical={DIALOG_PADDING}
        width={DIALOG_WIDTH}
      >
        <Dialog.Header paddingHorizontal={theme.sk.spacing.spaceMedium}>
          <Dialog.Title>
            {i18n.t('sales_portal.superintelligence.rename_title')}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body paddingHorizontal={DIALOG_PADDING}>
          <InputCompound size={INPUT_SIZE}>
            <InputCompound.Box>
              <InputCompound.Section>
                <InputCompound.Area
                  accessibilityLabel={placeholder}
                  maxLength={TITLE_MAX_LENGTH}
                  onChangeText={setDraft}
                  onSubmitEditing={handleSubmit}
                  placeholder={placeholder}
                  ref={inputRef}
                  value={draft}
                />
              </InputCompound.Section>
            </InputCompound.Box>
          </InputCompound>
        </Dialog.Body>
        <Dialog.Footer
          paddingHorizontal={DIALOG_PADDING}
          paddingTop={theme.sk.spacing.spaceMedium}
        >
          <XStack gap={theme.sk.spacing.spaceSmall} justifyContent="flex-end">
            <Dialog.Close asChild>
              <Button variant="secondary">{i18n.t('cancel')}</Button>
            </Dialog.Close>
            <Button disabled={!canSubmit} onPress={handleSubmit}>
              {i18n.t('save')}
            </Button>
          </XStack>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
